import * as React from 'react';
import { useState, useCallback } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Pencil as PencilIcon,
  Trash as TrashIcon,
  Upload as UploadIcon,
  Warning as WarningIcon,
} from '@phosphor-icons/react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import {
  applyHeaderAliases,
  evaluateImportGate,
  type HeaderMapping,
} from './lib/header-aliases';

// Columns that map to built-in fields — anything else is a custom field
const KNOWN_COLUMNS = new Set([
  'Unit', 'Name', 'Email', 'Phone', 'Phone 2', 'Phone2',
  'Vehicle', 'Delinquent', 'Has already logged in', 'Last login date',
  'unit_number', 'address', 'address2', 'city', 'state', 'zip_code',
]);

const ADDRESS_ALIASES: Record<string, keyof UnitAddressFields> = {
  address: 'address',
  'street address': 'address',
  'street_address': 'address',
  'street': 'address',
  'unit address': 'address',
  address2: 'address2',
  'address 2': 'address2',
  'street address 2': 'address2',
  city: 'city',
  state: 'state',
  zip: 'zip_code',
  zipcode: 'zip_code',
  'zip code': 'zip_code',
  zip_code: 'zip_code',
  postal: 'zip_code',
  'postal code': 'zip_code',
};

interface UnitAddressFields {
  unit_number: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip_code: string;
}

interface ParsedRow {
  [key: string]: string;
}

interface ImportResult {
  success: boolean;
  message: string;
  details?: {
    unitsCreated: number;
    usersCreated: number;
    customFieldsCreated: number;
    errors: string[];
  };
}

function parseUnitData(unitString: string): UnitAddressFields {
  const result: UnitAddressFields = {
    unit_number: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip_code: '',
  };
  const value = unitString.trim();

  if (!value) return result;

  if (/^address/i.test(value)) {
    const addressPart = value.replace(/^address/i, '').trim();
    if (addressPart.includes('#')) {
      const [street, unitNum] = addressPart.split('#', 2);
      result.address = street.trim();
      result.unit_number = unitNum.trim();
    } else {
      result.address = addressPart;
      const parts = addressPart.split(/\s+/, 2);
      result.unit_number = /^\d+$/.test(parts[0] ?? '') ? parts[0] : addressPart.slice(0, 12);
    }
  } else if (/^unit/i.test(value) && /-\s*address/i.test(value)) {
    const [unitPart, addressPartRaw] = value.split(/-\s*address/i, 2);
    const unitNum = unitPart.replace(/unit/i, '').replace('#', '').trim();
    const addressPart = addressPartRaw.trim();
    const fullAddressMatch = addressPart.match(/^(.+?)\s+([A-Z][A-Za-z\s]+?)\s+([A-Z]{2})\s+(\d{5})\s*$/i);

    if (fullAddressMatch) {
      result.address = fullAddressMatch[1].trim();
      result.city = fullAddressMatch[2].trim();
      result.state = fullAddressMatch[3].toUpperCase();
      result.zip_code = fullAddressMatch[4];
    } else {
      result.address = addressPart;
    }

    const [streetPrefix] = result.address.split(/\s+/, 1);
    const prefix = /^\d+$/.test(streetPrefix ?? '')
      ? streetPrefix
      : result.address.replace(/\s+/g, '').slice(0, 8);
    result.unit_number = `${prefix}-${unitNum}`;
  } else {
    result.unit_number = value.replace(/unit/i, '').replace(/[\s#]+/g, '').trim();
  }

  return result;
}

function parseUnitNumber(unitString: string): string {
  if (!unitString) return '';
  if (/-\s*address/i.test(unitString)) {
    const [unitPart] = unitString.split(/-\s*address/i, 1);
    return unitPart.replace(/unit/i, '').replace('#', '').trim();
  }
  return unitString.replace(/unit/i, '').replace('#', '').trim();
}

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase();
}

function isKnownColumn(column: string): boolean {
  return KNOWN_COLUMNS.has(column) || Boolean(ADDRESS_ALIASES[normalizeHeader(column)]);
}

function normalizeImportedRow(row: ParsedRow): ParsedRow {
  const normalized: ParsedRow = {};

  Object.entries(row).forEach(([key, rawValue]) => {
    const trimmedKey = key.trim();
    const value = String(rawValue ?? '').trim();
    const addressKey = ADDRESS_ALIASES[normalizeHeader(trimmedKey)];

    if (addressKey) {
      normalized[addressKey] = normalized[addressKey] || value;
      return;
    }

    normalized[trimmedKey] = value;
  });

  const parsed = parseUnitData(normalized['Unit'] ?? '');
  (['unit_number', 'address', 'address2', 'city', 'state', 'zip_code'] as const).forEach((key) => {
    if (!normalized[key] && parsed[key]) {
      normalized[key] = parsed[key];
    }
  });

  return normalized;
}

function formatAddress(row: ParsedRow): string {
  return [row.address, row.address2, row.city, row.state, row.zip_code]
    .filter(Boolean)
    .join(', ');
}

function getDisplayUnitNumber(row: ParsedRow): string {
  return parseUnitNumber(row['Unit'] ?? '') || row.unit_number || '';
}

const CSV_TEMPLATE_HEADERS = ['Unit', 'Name', 'Email', 'Phone', 'Phone 2', 'Vehicle', 'Delinquent'];

function isSpreadsheetFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}

function normalizeParsedRows(data: ParsedRow[], fields: string[]): {
  normalizedRows: ParsedRow[];
  columns: string[];
  extraColumns: string[];
  filteredRows: ParsedRow[];
} {
  const normalizedRows = data.map(normalizeImportedRow);
  const columns = Array.from(
    new Set([
      ...fields.map((field) => field.trim()).filter(Boolean),
      ...normalizedRows.flatMap((row) => Object.keys(row)),
    ])
  );
  const extraColumns = columns.filter((c) => !isKnownColumn(c));
  const filteredRows = normalizedRows.filter(
    (r) => r['Unit']?.trim() || r.address?.trim() || r['Name']?.trim() || r['Email']?.trim()
  );

  return { normalizedRows, columns, extraColumns, filteredRows };
}

function parseWorksheetRows(worksheet: XLSX.WorkSheet): { rows: ParsedRow[]; fields: string[] } {
  const sheetRows = XLSX.utils.sheet_to_json<Array<string | number | boolean | Date | null>>(worksheet, {
    blankrows: false,
    defval: '',
    header: 1,
    raw: false,
  });

  const nonEmptyRows = sheetRows.filter((row) => row.some((cell) => String(cell ?? '').trim() !== ''));
  const rowsWithoutPreamble =
    nonEmptyRows[0]?.length === 1 && String(nonEmptyRows[0][0] ?? '').trim() === 'Table 1'
      ? nonEmptyRows.slice(1)
      : nonEmptyRows;
  const [headerRow, ...dataRows] = rowsWithoutPreamble;
  const fields = (headerRow ?? []).map((cell) => String(cell ?? '').trim());
  const rows = dataRows.map((row) =>
    fields.reduce<ParsedRow>((acc, field, index) => {
      if (field) {
        acc[field] = String(row[index] ?? '').trim();
      }

      return acc;
    }, {})
  );

  return { rows, fields };
}

function downloadCsvTemplate() {
  const csv = CSV_TEMPLATE_HEADERS.join(',') + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'import-template.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function CommunityImportPage(): React.JSX.Element {
  const { community } = useUser();
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [extraColumns, setExtraColumns] = useState<string[]>([]);
  const [headerMappings, setHeaderMappings] = useState<HeaderMapping[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editBuffer, setEditBuffer] = useState<ParsedRow>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);

  const applyParsedRows = useCallback((data: ParsedRow[], fields: string[], file: File) => {
    const parsed = normalizeParsedRows(data, fields);

    // Rename near-miss headers (e.g. "Name 1" -> "Name") so people import
    // instead of silently becoming custom fields on a units-only import.
    // Alias before filtering so rows whose only data lives under a near-miss
    // header aren't dropped as empty.
    const aliased = applyHeaderAliases(parsed.normalizedRows, parsed.columns);
    const aliasedRows = aliased.rows.filter(
      (r) => r['Unit']?.trim() || r.address?.trim() || r['Name']?.trim() || r['Email']?.trim()
    );
    const extraColumns = aliased.columns.filter((c) => !isKnownColumn(c));

    setColumns(aliased.columns);
    setExtraColumns(extraColumns);
    setRows(aliasedRows);
    setHeaderMappings(aliased.mappings);
    setSelectedFile(file);
    setResult(null);

    if (aliased.mappings.length > 0) {
      toast.info(
        `Mapped ${aliased.mappings.map((m) => `"${m.from}" → ${m.to}`).join(', ')} so residents import correctly.`
      );
    }

    if (extraColumns.length > 0) {
      toast.info(
        `${extraColumns.length} extra column${extraColumns.length > 1 ? 's' : ''} detected — they'll be imported as custom fields: ${extraColumns.join(', ')}`
      );
    }
  }, []);

  const processFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (isSpreadsheetFile(file)) {
        try {
          const workbook = XLSX.read(e.target?.result, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];

          if (!firstSheetName) {
            toast.error('Could not parse the spreadsheet. No worksheet was found.');
            return;
          }

          const worksheet = workbook.Sheets[firstSheetName];
          const { rows: spreadsheetRows, fields } = parseWorksheetRows(worksheet);
          applyParsedRows(spreadsheetRows, fields, file);
        } catch {
          toast.error('Could not parse the spreadsheet. Make sure it is a valid Excel file.');
        }

        return;
      }

      let text = e.target?.result as string;
      // Strip "Table 1" preamble line if present
      const lines = text.split('\n');
      if (lines[0].trim() === 'Table 1') {
        text = lines.slice(1).join('\n');
      }

      Papa.parse<ParsedRow>(text, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data, meta }) => {
          applyParsedRows(data, meta.fields ?? [], file);
        },
        error: () => toast.error('Could not parse the file. Make sure it is a valid CSV or Excel file.'),
      });
    };
    if (isSpreadsheetFile(file)) {
      reader.readAsArrayBuffer(file);
      return;
    }

    reader.readAsText(file);
  }, [applyParsedRows]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
    onDrop: (accepted) => {
      const file = accepted[0];
      if (file) processFile(file);
      else toast.error('Please upload a CSV or Excel file.');
    },
  });

  const handleImport = async () => {
    if (!rows.length || !community) return;
    setImporting(true);
    setImportProgress(10);
    try {
      const response = await axios.put(
        `/api/v2/communities/${community.id}/import-residents`,
        { residents_data: rows },
        {
          onUploadProgress: (e) => {
            if (e.total) setImportProgress(Math.round((e.loaded / e.total) * 80));
          },
        }
      );
      setImportProgress(100);
      setResult(response.data);
      const errorCount = response.data?.details?.errors?.length ?? 0;
      if (response.data?.success) {
        toast.success('Import completed!');
      } else if (errorCount > 0) {
        toast.error(
          `Import finished with ${errorCount} failed row${errorCount > 1 ? 's' : ''}. Review the errors below.`
        );
      } else {
        toast.error(response.data?.message ?? 'Import failed. Please try again.');
      }
      setRows([]);
      setSelectedFile(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Import failed. Please try again.';
      toast.error(msg);
      setResult({ success: false, message: msg });
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const reset = () => {
    setRows([]);
    setColumns([]);
    setExtraColumns([]);
    setHeaderMappings([]);
    setSelectedFile(null);
    setResult(null);
    setEditingIndex(null);
  };

  const startEdit = (i: number) => {
    setEditingIndex(i);
    setEditBuffer({ ...rows[i] });
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...rows];
    updated[editingIndex] = editBuffer;
    setRows(updated);
    setEditingIndex(null);
  };

  const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));

  const validCount = rows.filter((r) => r['Name']?.trim() && r['Email']?.trim()).length;
  const unitOnlyCount = rows.filter((r) => r['Unit']?.trim() && (!r['Name']?.trim() || !r['Email']?.trim())).length;
  const addressCount = rows.filter((r) => formatAddress(r)).length;
  const importGate = evaluateImportGate(rows);

  return (
    <Stack spacing={3}>
      {/* Header card */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Import Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Upload a CSV or Excel file to import units and residents. Columns beyond the standard set are
              automatically created as custom fields.
            </Typography>
          </Box>
          <Button
            onClick={downloadCsvTemplate}
            size="small"
            startIcon={<DownloadIcon size={16} />}
            variant="outlined"
          >
            Download CSV Template
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Drop zone */}
            {!selectedFile && (
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? 'action.hover' : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <input {...getInputProps()} />
                <UploadIcon color="var(--mui-palette-primary-main)" size={40} />
                <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
                  {isDragActive ? 'Drop the file here' : 'Drag & drop a CSV or Excel file here, or click to browse'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supported: .csv, .xlsx, .xls — max 10 MB
                </Typography>
              </Box>
            )}

            {/* File info bar */}
            {selectedFile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CheckCircleIcon color="var(--mui-palette-success-main)" size={20} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(selectedFile.size / 1024).toFixed(1)} KB &middot; {rows.length} rows detected
                    </Typography>
                  </Box>
                </Stack>
                <Button size="small" color="inherit" onClick={reset}>
                  Remove
                </Button>
              </Box>
            )}

            {/* Header-mapping notice */}
            {headerMappings.length > 0 && (
              <Alert icon={<InfoIcon size={18} />} severity="success" sx={{ alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column{headerMappings.length > 1 ? 's' : ''} automatically mapped
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {headerMappings.map((m) => (
                    <Chip key={m.from} label={`${m.from} → ${m.to}`} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Alert>
            )}

            {/* Import gate: people data present but no resident would be created */}
            {importGate.level !== 'ok' && (
              <Alert
                icon={<WarningIcon size={18} />}
                severity={importGate.level === 'blocked' ? 'error' : 'warning'}
                sx={{ alignItems: 'flex-start' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {importGate.level === 'blocked' ? 'Import blocked — no residents would be created' : 'Heads up'}
                </Typography>
                <Typography variant="body2">{importGate.message}</Typography>
              </Alert>
            )}

            {/* Extra-columns notice */}
            {extraColumns.length > 0 && (
              <Alert
                icon={<InfoIcon size={18} />}
                severity="info"
                sx={{ alignItems: 'flex-start' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {extraColumns.length} custom field{extraColumns.length > 1 ? 's' : ''} will be created
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {extraColumns.map((col) => (
                    <Chip key={col} label={col} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Alert>
            )}

            {/* Summary row */}
            {rows.length > 0 && (
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {rows.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total rows
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: validCount > 0 ? 'success.main' : importGate.level === 'blocked' ? 'error.main' : 'text.primary',
                    }}
                  >
                    {validCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Residents (name + email)
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {unitOnlyCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Units only (no resident)
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {addressCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Addresses detected
                  </Typography>
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Preview table */}
      {rows.length > 0 && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Preview — {rows.length} rows
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Edit or remove rows before importing
            </Typography>
          </Box>

          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 80 }}>Unit #</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Address</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Name</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Phone</TableCell>
                  <TableCell sx={{ minWidth: 80 }}>Delinquent</TableCell>
                  {extraColumns.map((col) => (
                    <TableCell key={col} sx={{ minWidth: 120 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{col}</span>
                        <Tooltip title="Custom field">
                          <Chip label="custom" size="small" color="primary" variant="soft" sx={{ height: 16, fontSize: 10 }} />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ width: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => {
                  const isEditing = editingIndex === i;
                  const data = isEditing ? editBuffer : row;
                  const unitNum = getDisplayUnitNumber(data);
                  const address = formatAddress(data);

                  return (
                    <TableRow key={i} hover sx={{ bgcolor: isEditing ? 'action.selected' : undefined }}>
                      <TableCell>
                        <Chip label={unitNum || '—'} size="small" color="default" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Stack spacing={0.75}>
                            <TextField
                              size="small"
                              label="Address"
                              value={editBuffer.address ?? ''}
                              onChange={(e) => setEditBuffer({ ...editBuffer, address: e.target.value })}
                              sx={{ width: 180 }}
                            />
                            <Stack direction="row" spacing={0.75}>
                              <TextField
                                size="small"
                                label="City"
                                value={editBuffer.city ?? ''}
                                onChange={(e) => setEditBuffer({ ...editBuffer, city: e.target.value })}
                                sx={{ width: 90 }}
                              />
                              <TextField
                                size="small"
                                label="State"
                                value={editBuffer.state ?? ''}
                                onChange={(e) => setEditBuffer({ ...editBuffer, state: e.target.value })}
                                sx={{ width: 72 }}
                              />
                            </Stack>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color={address ? 'text.secondary' : 'text.disabled'}>
                            {address || '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editBuffer['Name'] ?? ''}
                            onChange={(e) => setEditBuffer({ ...editBuffer, Name: e.target.value })}
                            sx={{ width: 140 }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: data['Name'] ? 500 : 'normal', color: data['Name'] ? 'text.primary' : 'text.disabled' }}>
                            {data['Name'] || '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editBuffer['Email'] ?? ''}
                            onChange={(e) => setEditBuffer({ ...editBuffer, Email: e.target.value })}
                            sx={{ width: 180 }}
                          />
                        ) : (
                          <Typography variant="body2" color={data['Email'] ? 'text.secondary' : 'text.disabled'}>
                            {data['Email'] || '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editBuffer['Phone'] ?? ''}
                            onChange={(e) => setEditBuffer({ ...editBuffer, Phone: e.target.value })}
                            sx={{ width: 120 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {data['Phone'] && data['Phone'] !== 'N/A' ? data['Phone'] : '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {data['Delinquent'] && data['Delinquent'].toLowerCase() === 'yes' ? (
                          <Chip label="Yes" size="small" color="error" variant="soft" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">No</Typography>
                        )}
                      </TableCell>
                      {extraColumns.map((col) => (
                        <TableCell key={col}>
                          {isEditing ? (
                            <TextField
                              size="small"
                              value={editBuffer[col] ?? ''}
                              onChange={(e) => setEditBuffer({ ...editBuffer, [col]: e.target.value })}
                              sx={{ width: 120 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {data[col] && data[col] !== 'N/A' ? data[col] : '—'}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        {isEditing ? (
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Button size="small" variant="contained" onClick={saveEdit} sx={{ minWidth: 0, px: 1.5 }}>
                              Save
                            </Button>
                            <Button size="small" onClick={() => setEditingIndex(null)} sx={{ minWidth: 0, px: 1 }}>
                              Cancel
                            </Button>
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Edit row">
                              <IconButton size="small" onClick={() => startEdit(i)}>
                                <PencilIcon size={15} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove row">
                              <IconButton size="small" color="error" onClick={() => removeRow(i)}>
                                <TrashIcon size={15} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Import button */}
          <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            {importing && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Importing… {importProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={importProgress} />
              </Box>
            )}
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button variant="outlined" onClick={reset} disabled={importing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<UploadIcon size={16} />}
                onClick={handleImport}
                disabled={importing || rows.length === 0 || importGate.level === 'blocked'}
              >
                {importing ? 'Importing…' : `Import ${rows.length} rows`}
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Result */}
      {result && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            {result.success ? (
              <CheckCircleIcon color="var(--mui-palette-success-main)" size={22} />
            ) : (
              <WarningIcon color="var(--mui-palette-error-main)" size={22} />
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Import Results
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Alert severity={result.success ? 'success' : 'error'} sx={{ mb: 2 }}>
              {result.message}
            </Alert>

            {result.details && (
              <Stack spacing={2}>
                <Stack direction="row" spacing={4}>
                  {[
                    { label: 'Units', value: result.details.unitsCreated },
                    { label: 'Residents', value: result.details.usersCreated },
                    { label: 'Custom fields', value: result.details.customFieldsCreated },
                  ].map(({ label, value }) => (
                    <Box key={label} textAlign="center">
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {result.details.errors.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                        {result.details.errors.length} row{result.details.errors.length > 1 ? 's' : ''} had errors
                      </Typography>
                      <Stack spacing={0.5}>
                        {result.details.errors.map((e, i) => (
                          <Typography key={i} variant="body2" color="error">
                            • {e}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}
              </Stack>
            )}

            <Box sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={reset}>
                Import Another File
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Stack>
  );
}
